import {AssetType, Common, PageConstants, SystemAssetType} from '@taopix/taopix-core';
import {
  AssetMetaInterface,
  CalendarData,
  CellStructure,
  EventSetLink,
  EventSetLinks,
  PageInterface,
  ProductLayout,
  ReducerState,
  ScrapbookClippingInterface,
} from '@taopix/taopix-core/dist/types/types';
import {getPageName} from '../Utils/Common';
import {GenericListItem, StickerInterface} from '../types';

/**
 * Returns if the project is dirty or not.
 *
 * @param {ReducerState} state
 *
 * @return true if the project is dirty.
 */
const ProjectDirtySelector = (state: ReducerState): boolean => {
  return state.project.dirty;
};

/**
 * Returns a list of pages sorted by cover/pagenumber
 *
 * @param   {ReducerState}   state
 *
 * @return  {PageInterface[]}
 */
const PagesSelector = (state: ReducerState): PageInterface[] => {
  const pages: PageInterface[] = Object.values(state.pages.pages);
  const cover = pages.find(page => page.pagenumber === PageConstants.FrontCoverPageNumber);
  if (cover) {
    return [cover].concat(
      pages
        .filter(page => page.pagenumber !== PageConstants.FrontCoverPageNumber)
        .sort((a, b) => a.pagenumber - b.pagenumber)
    );
  }
  return pages.sort((a, b) => a.pagenumber - b.pagenumber);
};

/**
 * Return a list of ordered page numbers
 *
 * @param   {ReducerState[]}  state
 *
 * @return  {number[]}
 */
const PageListSelector = (state: ReducerState): number[] => {
  return PagesSelector(state).map(page => page.pagenumber);
};

/**
 * Returns an object of pagenumber->page name entries
 *
 * @param   {ReducerState[]}  state
 *
 * @return {{pageNumber: number; pageName: string}[]}
 */
const NamedPageListSelector = (state: ReducerState): {pageNumber: number; pageName: string}[] => {
  return PagesSelector(state).map(page => {
    return {
      pageNumber: page.pagenumber,
      pageName: getPageName(page, state.project.productLayout as ProductLayout),
    };
  }, {});
};

const PageCountSelector = (state: ReducerState): number => {
  return PagesSelector(state).length;
};

/**
 * Returns the total number of pages.
 * If the project is a Retro Prints project, then include the page quantities.
 *
 * @param state ReducerState.
 */
const TotalPageCountSelector = (state: ReducerState) => {
  const {
    project: {productLayout},
  } = state;
  if (!productLayout) return 0;
  if (!productLayout.retroprints) return productLayout.pagecount;

  return PagesSelector(state).reduce((accumulator: number, page: PageInterface) => {
    accumulator += 0 === page.qty ? 1 : page.qty;
    return accumulator;
  }, 0);
};

/**
 * Returns the product layout
 *
 * @param   {ReducerState}   state
 *
 * @return  {ProductLayout}
 */
const LayoutSelector = (state: ReducerState) => state.project.productLayout as ProductLayout;

/**
 * Returns a list of user assets
 *
 * @param   {ReducerState}   state
 *
 * @return  {AssetMetaInterface[]}
 */
const UserAssetSelector = (state: ReducerState): AssetMetaInterface[] => {
  const allAssets: AssetMetaInterface[] = Object.values(state.project.assets);
  const userAssets: AssetMetaInterface[] = allAssets.filter(
    asset => asset.assetType === AssetType.User
  ) as unknown as AssetMetaInterface[];
  return userAssets;
};

/**
 * Returns the external assets added to the project.
 *
 * @param  {ReducerState} state Current state.
 * @return  {AssetMetaInterface[]} List of external assets.
 */
const ExternalAssetSelector = (state: ReducerState): AssetMetaInterface[] => {
  return Object.values(state.project.assets).filter(asset => {
    return AssetType.User === asset.assetType && asset?.externalId && '' !== asset.externalId;
  }) as unknown as AssetMetaInterface[];
};

/**
 * Returns the project backgrounds
 *
 * @param   {ReducerState[]}        state  [state description]
 * @return  {AssetMetaInterface[]}         [return description]
 */
const BackgroundsSelector = (state: ReducerState): AssetMetaInterface[] => {
  return Object.values<AssetMetaInterface>(state.project.systemAssets).filter(
    a => a.assetType === AssetType.Background && !a.hiddenFromUser && a.active
  ) as AssetMetaInterface[];
};

/**
 * Reduce down & return the product backgrounds categories into a unique list
 *
 * @param   {ReducerState<string>}  state
 *
 * @return  {Record<string>}
 */
const BackgroundCategoriesSelector = (state: ReducerState): Record<string, GenericListItem> => {
  return (
    BackgroundsSelector(state)?.reduce(
      (accumulator: {[key: string]: GenericListItem}, background: AssetMetaInterface) => {
        if ('' !== background.categoryName) {
          const category = Common.getCurrentLocaleString(background.categoryName as string, true);
          if (!accumulator[category]) {
            accumulator[category] = {
              label: `${category}`,
              value: `${background.categoryName}`,
              key: `${Object.keys(accumulator).length}`,
            };
          }
        }
        return accumulator;
      },
      {}
    ) || {}
  );
};

const StickersSelector = (state: ReducerState) => {
  const omissions = Object.values(SystemAssetType).filter(type => '' !== type) as SystemAssetType[];
  return [
    ...Object.values<AssetMetaInterface>(state.project.systemAssets).filter(asset => {
      return (
        AssetType.ScrapbookImage === asset.assetType &&
        !asset.hiddenFromUser &&
        asset.active &&
        !omissions.includes(asset.systemAssetType)
      );
    }),
    ...Object.values<ScrapbookClippingInterface>(state.project.scrapbookClippings).filter(
      clipping => {
        return !clipping.hiddenFromUser;
      }
    ),
  ].reduce(
    (accumulator: StickerInterface[], current: AssetMetaInterface | ScrapbookClippingInterface) => {
      const isClipping = current.hasOwnProperty('clippingHeight');

      accumulator.push({
        id: current.id,
        assetRef: isClipping ? current.ref : ((current as AssetMetaInterface).assetRef as string),
        thumbnailsrc: isClipping
          ? (current as ScrapbookClippingInterface).thumbnail
          : (current as AssetMetaInterface).thumbnailsrc,
        name: current.name || '',
        categoryName:
          (isClipping
            ? (current as ScrapbookClippingInterface).category
            : (current as AssetMetaInterface).categoryName) || '',
        assetType: isClipping ? AssetType.ScrapbookClipping : AssetType.ScrapbookImage,
        asset: current,
      });

      return accumulator;
    },
    [] as StickerInterface[]
  );
};

const StickerCategoriesSelector = (state: ReducerState) => {
  return StickersSelector(state).reduce(
    (accumulator: {[key: string]: GenericListItem}, scrapbook: StickerInterface) => {
      const category = Common.getCurrentLocaleString(scrapbook.categoryName as string, true);
      if (category !== '' && !accumulator[category]) {
        accumulator[category] = {
          label: `${category}`,
          value: `${scrapbook.categoryName}`,
          key: `${Object.keys(accumulator).length}`,
        };
      }
      return accumulator;
    },
    {}
  );
};

/**
 * Returns a list of chargable calendar event sets used in the project.
 *
 * @param  {ReducerState} state Current state.
 * @return  {EventSetLink[]} List of chargable event sets.
 */
const ChargableEventSetSelector = (state: ReducerState): EventSetLink[] => {
  return Object.values(state.project.calendar?.eventSetLinks || {}).filter(eventSet => {
    return Boolean((eventSet as any).chargeable);
  });
};

/**
 * Returns the Perfectly Clear setting value.
 *
 * @param  {ReducerState} state Current state.
 * @return  {boolean} True if Perfectly Clear should be applied to the project.
 */
const PerfectlyClearSelector = (state: ReducerState) => state.project.perfectlyClear;

/**
 * Returns the asset services available on the project
 *
 * @param  {ReducerState} state Current state.
 * @return  {AssetService[]}
 */
const AssetServicesSelector = (state: ReducerState) => {
  return Object.values(state.assetServices).sort((a, b) => {
    return Math.abs(a.id) < Math.abs(b.id) ? -1 : Math.abs(a.id) > Math.abs(b.id) ? 1 : 0;
  });
};

/**
 * Returns the calendar layout
 *
 * @param   {ReducerState}   state
 *
 * @return  {CalendarData}
 */
const CalendarSelector = (state: ReducerState) => state.project.calendar as CalendarData;

/**
 * Returns the selected shape
 *
 * @param   {ReducerState}   state
 *
 * @return  {ComputedPageItem}
 */
const SelectedShapeSelector = (state: ReducerState) => (state.project as any)?.selectedItem;

/**
 * Returns the calendar eventSetLinks
 *
 * @param   {ReducerState}   state
 *
 * @return  {EventSetLinks}
 */
const CalendarEventSetLinkSelector = (state: ReducerState) =>
  state.project.calendar?.eventSetLinks as EventSetLinks;

/**
 * Returns the first calendar cell structure entry
 *
 * @param   {ReducerState}   state
 *
 * @return  {CellStructureItem}
 */
const CalendarCellStructureSelector = (state: ReducerState) =>
  Object.values(state.project?.calendar?.cellStructure as CellStructure)[0];

export {
  ProjectDirtySelector,
  AssetServicesSelector,
  PagesSelector,
  LayoutSelector,
  UserAssetSelector,
  BackgroundsSelector,
  PerfectlyClearSelector,
  ExternalAssetSelector,
  PageListSelector,
  NamedPageListSelector,
  ChargableEventSetSelector,
  PageCountSelector,
  TotalPageCountSelector,
  CalendarSelector,
  BackgroundCategoriesSelector,
  StickersSelector,
  StickerCategoriesSelector,
  SelectedShapeSelector,
  CalendarEventSetLinkSelector,
  CalendarCellStructureSelector,
};
